import { Tasks } from './tasks';
import { Notes } from './notes';
import { Files } from './files';
import { Emails } from './emails';
import { Overview } from './overview';
import { Messages } from './messages';
import { Calendar } from './calendar';
import { Settings } from './settings';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, firestore } from '../firebase-config';
import { doc, onSnapshot } from 'firebase/firestore';
import { DesktopOnlyMessage, VerticalTabs } from './widgets';
import { MicrosoftAuth, GoogleAuth, AuthProvider, authManagerInstance } from './values';

export function Dashboard({ setAuthStatus, appMode, toggleAppMode }) {
    const [user, setUser] = useState(null);
    const [events, setEvents] = useState([]);
    const [emails, setEmails] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [authManager] = useState(() => authManagerInstance);//eslint-disable-next-line
    const [subscriptionTier, setSubscriptionTier] = useState(() => authManager.subscriptionTier);
    const [isGoogleLoggedIn, setIsGoogleLoggedIn] = useState(() => {
        const googleAccounts = authManagerInstance.getAccounts(AuthProvider.GOOGLE);
        return googleAccounts.length > 0 ? GoogleAuth.LOGGED_IN : GoogleAuth.NOT_LOGGED_IN;
    });
    const [isMicrosoftLoggedIn, setIsMicrosoftLoggedIn] = useState(() => {
        const microsoftAccounts = authManagerInstance.getAccounts(AuthProvider.MICROSOFT);
        return microsoftAccounts.length > 0 ? MicrosoftAuth.LOGGED_IN : MicrosoftAuth.NOT_LOGGED_IN;
    });
    const [providerAccounts, setProviderAccounts] = useState({
        [AuthProvider.GOOGLE]: null,
        [AuthProvider.MICROSOFT]: null
    });
    const [firebaseUser, setFirebaseUser] = useState(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setFirebaseUser(user);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!firebaseUser) return;
        const userDocRef = doc(firestore, 'users', firebaseUser.uid);
        const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
                const userData = docSnapshot.data();
                let newTier = 0;
                if (userData.isStarter) newTier = 1;
                if (userData.isPro) newTier = 2;
                if (userData.isEnterprise) newTier = 3;
                setSubscriptionTier(newTier);
                if (authManager.subscriptionTier !== newTier) {
                    authManager.setSubscriptionTier(newTier);
                }
            }
        }, (error) => { });
        return () => unsubscribe();//eslint-disable-next-line
    }, [firebaseUser]);

    useEffect(() => {
        const updateAuthState = () => {
            const googleAccounts = authManager.getAccounts(AuthProvider.GOOGLE);
            const microsoftAccounts = authManager.getAccounts(AuthProvider.MICROSOFT);
            const allAccounts = authManager.getAccounts();
            setSubscriptionTier(authManager.subscriptionTier);
            setAccounts(allAccounts);
            setIsGoogleLoggedIn(googleAccounts.length > 0 ? GoogleAuth.LOGGED_IN : GoogleAuth.NOT_LOGGED_IN);
            setIsMicrosoftLoggedIn(microsoftAccounts.length > 0 ? MicrosoftAuth.LOGGED_IN : MicrosoftAuth.NOT_LOGGED_IN);
            const newActiveAccounts = { ...providerAccounts };
            const activeGoogleAccount = authManager.getActiveAccount(AuthProvider.GOOGLE);
            if (activeGoogleAccount) {
                newActiveAccounts[AuthProvider.GOOGLE] = activeGoogleAccount;
                if (!user && activeGoogleAccount.userData) {
                    setUser(activeGoogleAccount.userData);
                }
            } else {
                newActiveAccounts[AuthProvider.GOOGLE] = null;
            }
            const activeMicrosoftAccount = authManager.getActiveAccount(AuthProvider.MICROSOFT);
            if (activeMicrosoftAccount) {
                newActiveAccounts[AuthProvider.MICROSOFT] = activeMicrosoftAccount;
                if (!user && !activeGoogleAccount && activeMicrosoftAccount.userData) {
                    setUser(activeMicrosoftAccount.userData);
                }
            } else {
                newActiveAccounts[AuthProvider.MICROSOFT] = null;
            }
            setProviderAccounts(newActiveAccounts);
            if (allAccounts.length === 0) {
                setUser(null);
            }
        };
        updateAuthState();
        authManager.addStateChangeListener(updateAuthState);
        window.addEventListener('authStateChanged', updateAuthState);
        return () => {
            authManager.removeStateChangeListener(updateAuthState);
            window.removeEventListener('authStateChanged', updateAuthState);
            authManager.cleanup();
        };//eslint-disable-next-line
    }, [user]);

    const tabs = [
        { label: 'Overview', content: <Overview appMode={appMode} setAuthStatus={setAuthStatus} events={events} setEvents={setEvents} setError={setError} authManager={authManager} setActiveTab={setActiveTab} toggleAppMode={toggleAppMode} setIsGoogleLoggedIn={setIsGoogleLoggedIn} setIsMicrosoftLoggedIn={setIsMicrosoftLoggedIn} subscriptionTier={subscriptionTier} />, icon: <i className="bi bi-grid text-xl"></i> },
        { label: 'Tasks', content: <Tasks appMode={appMode} setAuthStatus={setAuthStatus} toggleAppMode={toggleAppMode} />, icon: <i className="bi bi-book-half text-xl"></i> },
        { label: 'Emails', content: <Emails appMode={appMode} authManager={authManager} user={user} setUser={setUser} accounts={accounts} setAccounts={setAccounts} loading={loading} setLoading={setLoading} emails={emails} setEmails={setEmails} error={error} setError={setError} isGoogleLoggedIn={isGoogleLoggedIn} isMicrosoftLoggedIn={isMicrosoftLoggedIn} setIsGoogleLoggedIn={setIsGoogleLoggedIn} setIsMicrosoftLoggedIn={setIsMicrosoftLoggedIn} providerAccounts={providerAccounts} setActiveTab={setActiveTab} subscriptionTier={subscriptionTier} />, icon: <i className="bi bi-envelope-open text-xl"></i> },
        { label: 'Messages', content: <Messages appMode={appMode} />, icon: <i className="bi bi-chat-left-text text-xl"></i> },
        { label: 'Notes', content: <Notes appMode={appMode} />, icon: <i className="bi bi-card-checklist text-xl"></i> },
        { label: 'Files', content: <Files appMode={appMode} error={error} setError={setError} loading={loading} setLoading={setLoading} accounts={accounts} setAccounts={setAccounts} user={user} setUser={setUser} setAuthStatus={setAuthStatus} isGoogleLoggedIn={isGoogleLoggedIn} isMicrosoftLoggedIn={isMicrosoftLoggedIn} setIsGoogleLoggedIn={setIsGoogleLoggedIn} authManager={authManager} providerAccounts={providerAccounts} setIsMicrosoftLoggedIn={setIsMicrosoftLoggedIn} setActiveTab={setActiveTab} toggleAppMode={toggleAppMode} subscriptionTier={subscriptionTier} />, icon: <i className="bi bi-file-earmark text-xl"></i> },
        { label: 'Calendar', content: <Calendar appMode={appMode} user={user} setUser={setUser} accounts={accounts} setAccounts={setAccounts} loading={loading} setLoading={setLoading} error={error} setError={setError} events={events} setEvents={setEvents} authManager={authManager} setIsGoogleLoggedIn={setIsGoogleLoggedIn} providerAccounts={providerAccounts} isGoogleLoggedIn={isGoogleLoggedIn} isMicrosoftLoggedIn={isMicrosoftLoggedIn} setIsMicrosoftLoggedIn={setIsMicrosoftLoggedIn} setActiveTab={setActiveTab} subscriptionTier={subscriptionTier} />, icon: <i className="bi bi-calendar text-xl"></i> },
        { label: 'Settings', content: <Settings appMode={appMode} toggleAppMode={toggleAppMode} setError={setError} setLoading={setLoading} setUser={setUser} isGoogleLoggedIn={isGoogleLoggedIn} isMicrosoftLoggedIn={isMicrosoftLoggedIn} setIsGoogleLoggedIn={setIsGoogleLoggedIn} setIsMicrosoftLoggedIn={setIsMicrosoftLoggedIn} authManager={authManager} accounts={accounts} setAccounts={setAccounts} setAuthStatus={setAuthStatus} providerAccounts={providerAccounts} />, icon: <i className="bi bi-gear text-xl"></i> },
    ];
    if (isMobile) {
        return <DesktopOnlyMessage />;
    }
    return <VerticalTabs tabs={tabs} appMode={appMode} activeTab={activeTab} setActiveTab={setActiveTab} />;
}